<template>
  <!-- The banner sits a main wrapper's gap below the field, as the screen's other blocks do -->
  <div class="kds-main-wrapper">
    <SearchSelectField
      v-bind="{ ...fieldAttrs.practiceSearch, ...$attrs }"
      v-model:selected-item="practice"
      :search-fn="searchPractices"
      :featured-fn="findFeaturedPractices"
      :item-key="itemKey"
      :item-title="itemTitle"
      :item-subtitle="itemSubtitle"
    >
      <template #option="{ item, segments, subtitleSegments }">
        <span class="doctor-practice-picker__option-title">
          <SearchSelectHighlight :segments="segments" />
        </span>
        <span
          v-if="itemSubtitle(item)"
          class="doctor-practice-picker__option-subtitle kds-typography-interactive-small"
        >
          <SearchSelectHighlight :segments="subtitleSegments" />
        </span>
      </template>
      <template #dropdown-footer>
        <button
          type="button"
          class="doctor-practice-picker__manual-entry kds-typography-interactive-medium"
          data-qa="doctor-practice-manual-entry"
          :data-dd-action-name="manualEntryActionName"
          @mousedown.prevent
          @click="emit('manual-entry')"
        >
          <EditOutlinedIcon class="doctor-practice-picker__manual-entry-icon" />
          {{ $t('eu__doctor_outreach_prescriber_search__manual_entry') }}
        </button>
      </template>
    </SearchSelectField>

    <BaseBanner
      v-if="practiceConfirmed"
      variant="success"
      data-qa="doctor-practice-search-selection-success"
      :title="itemTitle(practice)"
    >
      <p class="kds-typography-paragraph-medium">
        {{ itemSubtitle(practice) }}
      </p>
    </BaseBanner>
  </div>
</template>

<script setup>
// The doctor practice search of the app's outreach screen and of the action link page, so that a
// fix reaches both: the practices as options, the manual entry under them, the confirmation once
// the dropdown has closed, and the required error. The screens keep the query, the pick and the
// confirmed practice, and search the backend their own way.
import { computed, ref, watch } from 'vue'
import useForm from '@shared/composables/useForm.js'
import SearchSelectField from '@shared/components/form/SearchSelectField.vue'
import SearchSelectHighlight from '@shared/components/form/SearchSelectHighlight.vue'
import EditOutlinedIcon from '@shared/assets/icon/EditOutlinedIcon.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  // Both resolve to the backend's practices. A failed or canceled search goes back to
  // SearchSelectField as it is, which offers to try again or ignores it.
  searchFn: {
    type: Function,
    required: true,
  },
  featuredFn: {
    type: Function,
    required: true,
  },
  confirmedPracticeId: {
    type: Number,
    default: null,
  },
  manualEntryActionName: {
    type: String,
    default: null,
  },
  // A prop rather than an emit, because Vue drops an unmounted component's emits, and a practice
  // picked right before the screen moves on is still confirmed. Being a prop, it takes exactly one
  // @confirm handler: forwarded arrays of listeners and @confirm.once do not reach it.
  onConfirm: {
    type: Function,
    required: true,
  },
})

const emit = defineEmits(['manual-entry'])

const query = defineModel('query', {
  type: String,
  default: '',
})

const practice = defineModel('practice', {
  type: Object,
  default: null,
})

const DROPDOWN_CLOSE_ANIMATION_MS = 300

const normalizePractice = (item) => ({
  ...item,
  title: item?.name ?? '',
  subtitle: [
    item?.address,
    [item?.postal_code, item?.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', '),
})

const itemKey = (item) => String(item?.id ?? '')
const itemTitle = (item) => item?.title ?? ''
const itemSubtitle = (item) => item?.subtitle ?? ''

const searchPractices = async (searchQuery, signal) =>
  (await props.searchFn(searchQuery, signal)).map(normalizePractice)

const findFeaturedPractices = async () =>
  (await props.featuredFn()).map(normalizePractice)

const practiceConfirmed = computed(
  () =>
    typeof practice.value?.id === 'number' &&
    practice.value.id === props.confirmedPracticeId,
)

watch(practice, (picked) => {
  if (typeof picked?.id !== 'number') return
  setTimeout(() => props.onConfirm(picked), DROPDOWN_CLOSE_ANIMATION_MS)
})

// The state holds the query model itself, so useForm's field binding reads and writes the screen's
// query. The binding also brings the errors, which typing or leaving the field clears, the desktop
// autofocus, and the name that a failed submit scrolls to.
const form = ref({ practiceSearch: query })

const { fieldAttrs, submit, addErrors } = useForm(
  form,
  ref({ practiceSearch: {} }),
  {
    // Only submitted once the field lacks a practice, and failing is what makes useForm scroll to it
    onSubmit: () => {
      addErrors('practiceSearch', 'generic__validation_error__required')
      throw new Error('A practice from the list is required')
    },
    onSuccess: () => {},
    onError: () => {},
  },
)

// Picking the practice whose name was typed leaves the query unchanged, so no typing clears the
// required error. Validating the field again, as leaving it does, clears it, but only because the
// field has no validations: with validations: ['required'], resetting the pick would add an error.
watch(practice, () => fieldAttrs.value.practiceSearch.onBlur())

// Continue without a practice from the list marks the field as required. A practice picked but not
// yet confirmed waits for the dropdown to close.
const requirePractice = () => {
  if (practiceConfirmed.value) return practice.value
  if (query.value.trim().length === 0 || practice.value == null) submit()
  return null
}

defineExpose({ requirePractice })
</script>

<style lang="scss" scoped>
.doctor-practice-picker__option-title {
  color: $kds-color-text-primary;
}

.doctor-practice-picker__option-subtitle {
  display: block;
  color: $kds-color-text-secondary;
}

.doctor-practice-picker__manual-entry {
  display: flex;
  align-items: center;
  gap: $kds-space-0_5x;
  width: 100%;
  min-height: 34px;
  padding: $kds-space-1_5x $kds-space-2x;
  text-align: left;
  background: transparent;
  border: none;
  color: $kds-color-text-interactive-accent;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    outline: none;
    background-color: $kds-color-background-interactive-subtle-hover;
  }
}

.doctor-practice-picker__manual-entry-icon {
  display: block;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
</style>
